<template>
  <div>
    <div v-if="!consentGiven" :class="$style.consent">
      <h2>{{ storeTranslations.translations?.['consentHeading'] || fallbackConsentHeading }}</h2>
      <div v-html="vendorConsentText" />
      <Button1XP
        :class="$style.consentButton"
        size="s"
        :text="consentButtonLabel"
        @click="handleContentButton"
      />
    </div>
    <template v-if="consentGiven">
      <div :id="containerId" />
      <ReCaptcha v-if="config.useRecaptcha" :execute-switch="executeSwitch" :submit="getToken" />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, onUpdated, ref, watch } from 'vue';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import { MwfFormNamesAndIds } from '../../globals/constants/mwf';
import { getMarketingConsent } from '../../globals/cookiebot';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';
import { useStoreTranslations } from '../../store/translations/translations';
import Button1XP from '../Button1XP/Button1XP.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const storeTranslations = useStoreTranslations();
const dictionary = window.dictionary;

const vendor = cmpVendorIDs.GRECAPTCHA;
const containerId = ref(null);
const executeSwitch = ref(null);
const consentGiven = ref(window.Cookiebot ? getMarketingConsent() : getVendorConsent(vendor.id));

const vendorConsentText = computed(
  () =>
    storeTranslations.translations?.['consentText']
      ?.replaceAll('###VENDOR_NAME###', vendor.name)
      .replaceAll('#<VENDOR_NAME>#', vendor.name) || fallbackConsentText,
);
const consentButtonLabel = computed(
  () =>
    storeTranslations.translations?.['consentButtonLabel']?.replaceAll(/&apos;/g, "'") ||
    fallbackConsentButtonLabel,
);

const fallbackConsentHeading = computed(() => dictionary?.consentHeading);
const fallbackConsentText = dictionary?.consentText
  ?.replaceAll('###VENDOR_NAME###', vendor.name)
  .replaceAll('#<VENDOR_NAME>#', vendor.name);
const fallbackConsentButtonLabel = dictionary?.consentButtonLabel?.replaceAll(/&apos;/g, "'");

const formcentricScript = ref(null);
const isFCInitialised = ref(false);

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(vendor.id);
  checkConsent();
};

const handleContentButton = () => {
  if (window.cmpConsentType === 'consentmanager') {
    window.__cmp('setVendorConsent', [vendor.id, 1], () => {
      consentGiven.value = getVendorConsent(vendor.id);
    });
  } else {
    if (window.Cookiebot) {
      window.Cookiebot.onaccept = () => {
        consentGiven.value = getMarketingConsent();
      };
      window.Cookiebot.show();
    }
    checkConsent();
  }
};

const checkConsent = () => {
  if (consentGiven.value && props.config.fcScript) {
    initFormcentric();
  }
};

const getToken = (token) => {
  const tokenField = document.getElementById(MwfFormNamesAndIds.TOKEN);

  if (tokenField) {
    tokenField.value = token;
  }
};

const initFormcentric = () => {
  containerId.value = props.config.selector.replace('#', '');
  formcentricScript.value = props.config.fcScript;
  const allScripts = document.getElementsByTagName('script');

  for (const script of allScripts) {
    isFCInitialised.value = script.src === formcentricScript.value;
  }

  if (formcentricScript.value && !isFCInitialised.value) {
    const embedFormcentricScript = document.createElement('script');
    embedFormcentricScript.setAttribute('src', formcentricScript.value);
    document.body.appendChild(embedFormcentricScript);
    isFCInitialised.value = true;
  }
};

const registerConsentListenersWithDelay = (checkVendorConsent) => {
  const intervalId = setInterval(() => {
    if (typeof window.__cmp === 'function') {
      registerConsentListeners(checkVendorConsent);
      clearInterval(intervalId);
    }
  }, 100);
  setTimeout(() => clearInterval(intervalId), 5000);
};

watch(isFCInitialised, (newValue, oldValue) => {
  if (newValue !== oldValue && newValue) {
    if (window.formcentric) {
      window.formcentric.initFormcentric();
    }
  }
});

onMounted(() => {
  consentGiven.value = window.Cookiebot ? getMarketingConsent() : getVendorConsent(vendor.id);

  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListenersWithDelay(checkVendorConsent);
    setTimeout(() => {
      checkVendorConsent();
    }, 1000);
  } else {
    checkConsent();
  }
});

onUpdated(() => {
  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListeners(checkVendorConsent);
    checkVendorConsent();
  } else {
    checkConsent();
  }

  consentGiven.value = window.Cookiebot ? getMarketingConsent() : getVendorConsent(vendor.id);
});
</script>

<style module lang="scss">
@forward './MondayWebforms.scss';
</style>
